import React from 'react';

import './AppMenu.scss';

export interface AppMenuLink {
  label: string;
  id: string;
  active?: boolean;
  /** Host owns navigation. Mutually exclusive with `href`. */
  onClick?: () => void;
  /** Renders as `<a href>`. */
  href?: string;
  /** Only meaningful when `href` is set. */
  external?: boolean;
  /** Caps tag at the row's end, e.g. where the tool used to live. */
  tag?: string;
  tagTitle?: string;
  pinned?: boolean;
  /** Bootstrap Icons class; shown on rows outside a section (the pinned list). */
  icon?: string;
}

export interface AppMenuSection {
  id: string;
  header: string;
  /** Bootstrap Icons class. */
  icon?: string;
  links: ReadonlyArray<AppMenuLink>;
  open: boolean;
}

export interface AppMenuSearch {
  value: string;
  onChange: (value: string) => void;
  /** Enter in the box — the host opens the first visible link. */
  onSubmit?: () => void;
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  /** The box around the input, for the host to measure. */
  boxRef?: React.Ref<HTMLDivElement>;
  /** Shortcut hint drawn at the right end of the box. */
  hint?: string;
}

export interface AppMenuProps {
  /** Filter by permission before passing in. */
  sections: ReadonlyArray<AppMenuSection>;
  onToggleSection?: (id: string) => void;
  /** Rows listed above the sections under "Pinned"; empty hides the block. */
  pinned?: ReadonlyArray<AppMenuLink>;
  /** Present → every row gets a star that toggles its pin. */
  onTogglePin?: (id: string) => void;
  search?: AppMenuSearch;
  /** Shown instead of the sections when the search matches nothing. */
  emptyText?: string;
  className?: string;
}

/** `[before, match, after]` for the first search term, or null when it does not occur. */
export function splitHighlight(
  label: string,
  query: string,
): [string, string, string] | null {
  const term = query.trim().toLowerCase().split(/\s+/)[0];
  if (!term) return null;
  const at = label.toLowerCase().indexOf(term);
  if (at < 0) return null;
  return [label.slice(0, at), label.slice(at, at + term.length), label.slice(at + term.length)];
}

const Label: React.FC<{ label: string; query: string }> = ({ label, query }) => {
  const parts = splitHighlight(label, query);
  return (
    <span className="wb-menu__label">
      {parts ? (
        <>
          {parts[0]}
          <mark className="wb-menu__mark">{parts[1]}</mark>
          {parts[2]}
        </>
      ) : (
        label
      )}
    </span>
  );
};

interface RowProps {
  link: AppMenuLink;
  query: string;
  onTogglePin?: (id: string) => void;
  flat?: boolean;
}

const Row: React.FC<RowProps> = ({ link, query, onTogglePin, flat }) => {
  const cls = `wb-menu__link${link.active ? ' is-active' : ''}`;
  const body = (
    <>
      {flat && link.icon ? (
        <i className={`bi ${link.icon} wb-menu__link-icon`} aria-hidden />
      ) : null}
      <Label label={link.label} query={query} />
      {link.tag ? (
        <span className="wb-menu__tag" title={link.tagTitle}>
          {link.tag}
        </span>
      ) : null}
    </>
  );
  return (
    <li className="wb-menu__row">
      {link.href ? (
        <a
          href={link.href}
          target={link.external ? '_blank' : undefined}
          rel={link.external ? 'noreferrer' : undefined}
          className={cls}
          aria-current={link.active ? 'page' : undefined}
        >
          {body}
        </a>
      ) : (
        <button
          type="button"
          className={cls}
          onClick={link.onClick}
          aria-current={link.active ? 'page' : undefined}
        >
          {body}
        </button>
      )}
      {onTogglePin ? (
        <button
          type="button"
          className={`wb-menu__pin${link.pinned ? ' is-on' : ''}`}
          onClick={() => onTogglePin(link.id)}
          aria-pressed={!!link.pinned}
          aria-label={`${link.pinned ? 'Unpin' : 'Pin'} ${link.label}`}
        >
          <i className={`bi ${link.pinned ? 'bi-star-fill' : 'bi-star'}`} aria-hidden />
        </button>
      ) : null}
    </li>
  );
};

/** Accordion side menu with search and pinned rows. NOT from Claude Design. */
export const AppMenu: React.FC<AppMenuProps> = ({
  sections,
  onToggleSection,
  pinned = [],
  onTogglePin,
  search,
  emptyText = 'No matches',
  className,
}) => {
  const query = search?.value ?? '';
  const searching = query.trim() !== '';
  return (
    <nav
      className={`wb-menu${className ? ' ' + className : ''}`}
      aria-label="Primary navigation"
    >
      {search ? (
        <div className="wb-menu__search" ref={search.boxRef}>
          <i className="bi bi-search wb-menu__search-icon" aria-hidden />
          <input
            ref={search.inputRef}
            className="wb-menu__search-input"
            type="search"
            value={search.value}
            placeholder={search.placeholder ?? 'Search tools'}
            aria-label={search.placeholder ?? 'Search tools'}
            autoComplete="off"
            onChange={(e) => search.onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && search.onSubmit) {
                e.preventDefault();
                search.onSubmit();
              } else if (e.key === 'Escape' && search.value !== '') {
                e.preventDefault();
                search.onChange('');
              }
            }}
          />
          {search.hint && !search.value ? (
            <kbd className="wb-menu__kbd" aria-hidden>
              {search.hint}
            </kbd>
          ) : null}
        </div>
      ) : null}

      <div className="wb-menu__scroll">
        {!searching && pinned.length > 0 ? (
          <>
            <div className="wb-menu__sec-label">Pinned</div>
            <ul className="wb-menu__links wb-menu__links--flat">
              {pinned.map((link) => (
                <Row key={link.id} link={link} query="" onTogglePin={onTogglePin} flat />
              ))}
            </ul>
            <div className="wb-menu__sec-label">All tools</div>
          </>
        ) : null}

        {searching && sections.length === 0 ? (
          <p className="wb-menu__empty">{emptyText}</p>
        ) : null}

        {sections.map((section) => {
          const open = searching || section.open;
          const hasActive = section.links.some((l) => l.active);
          const listId = `wb-menu-sec-${section.id}`;
          return (
            <div
              key={section.id}
              className={`wb-menu__section${open ? ' is-open' : ''}${
                hasActive ? ' has-active' : ''
              }`}
            >
              <button
                type="button"
                className="wb-menu__header"
                onClick={() => onToggleSection?.(section.id)}
                aria-expanded={open}
                aria-controls={listId}
              >
                {section.icon ? (
                  <i className={`bi ${section.icon} wb-menu__header-icon`} aria-hidden />
                ) : null}
                <span className="wb-menu__header-label">{section.header}</span>
                <span className="wb-menu__count">{section.links.length}</span>
                <i className="bi bi-chevron-right wb-menu__chev" aria-hidden />
              </button>
              <div className="wb-menu__wrap" id={listId} inert={!open}>
                <ul className="wb-menu__links">
                  {section.links.map((link) => (
                    <Row key={link.id} link={link} query={query} onTogglePin={onTogglePin} />
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default AppMenu;
